// Dependencies
import React from 'react';

// External Components
import NextLink from 'next/link';
import CopyToClipboard from 'react-copy-to-clipboard';

// State management
import { logoutUser } from 'src/store/slices/auth';

// Hooks
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'src/store/store';

// Material UI Components
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// External Components
import Iconify from 'src/components/Iconify';

// Utils
import getFromObject from 'src/utils/getFromObject';

// Types
type Props = {
  onClick?: () => void;
};

/**
 * Generates a background color and a contrasting foreground (text) color from a string.
 * Useful for creating consistently styled avatars or elements based on text input.
 *
 * @param string The input string to generate colors from.
 * @returns An object containing `backgroundColor` (hex) and `color` (hex for text).
 */
function stringToBgColor(string: string): { backgroundColor: string; color: string } {
  let hash = 0;

  /* eslint-disable no-bitwise */
  for (let i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let backgroundColor = '#';
  const rgb = [];

  for (let i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    rgb.push(value);
    backgroundColor += value.toString(16).padStart(2, '0');
  }
  /* eslint-enable no-bitwise */

  // Calculate luminance of the background color to determine contrasting text color
  // Formula: L = 0.299*R + 0.587*G + 0.114*B
  // Threshold of 128: if L > 128, background is light, use dark text. Otherwise, use light text.
  const r = rgb[0];
  const g = rgb[1];
  const b = rgb[2];
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

  const textColor = luminance > 128 ? '#000000' : '#ffffff';

  return {
    backgroundColor,
    color: textColor,
  };
}

function stringAvatar(name: string) {
  const avatarName = name.split(' ');
  return {
    sx: {
      ...stringToBgColor(name),
    },
    children: avatarName.length > 1 ? `${avatarName[0][0]}${avatarName[1][0]}` : avatarName[0][0],
  };
}

/**
 * Account menu in the navbar
 */
export default function NavAccount({ onClick }: Props) {
  // ===================================
  // Redux
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();

  // ===================================
  // State
  const [copied, setCopied] = React.useState(false);

  // ===================================
  // Refs
  const timeoutRef = React.useRef<any>(null);

  // ===================================
  // Hooks
  const { t } = useTranslation('common');
  const router = useRouter();

  // ===================================
  // Handlers
  const logout = () => {
    dispatch(logoutUser());
  };

  // ===================================
  // Effects
  // Reset copied state
  React.useEffect(() => {
    if (copied) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [copied]);

  return (
    <Box sx={{ width: '100%', px: 2 }}>
      <Card sx={{ p: 2 }}>
        <Stack spacing={1} direction="row" alignItems="center">
          <Avatar {...stringAvatar(user?.name || 'User')} />
          <Box minWidth={0}>
            <Typography mb={0} variant="body2" noWrap dir="auto">
              {user && 'name' in user && user?.name}
            </Typography>
            <CopyToClipboard text={`${user?.uid || ''}`} onCopy={() => setCopied(true)}>
              <Button
                endIcon={
                  copied ? (
                    <Iconify
                      icon={'carbon:checkmark'}
                      color="primary.main"
                      width={12}
                      height={12}
                    />
                  ) : (
                    <Iconify icon={'carbon:copy'} color="text.secondary" width={12} height={12} />
                  )
                }
                size="small"
                variant="outlined"
                color="inherit"
                sx={{ p: 0, px: 1, maxWidth: '100%', minWidth: 150, width: '100%' }}
              >
                <Typography
                  component="p"
                  variant="body2"
                  sx={{
                    color: copied ? 'primary.main' : 'text.secondary',
                  }}
                  noWrap
                >
                  {user?.uid}
                </Typography>
              </Button>
            </CopyToClipboard>
          </Box>
        </Stack>
        <Box mt={2} />
        <Stack direction="column" spacing={1.5}>
          {router.pathname.includes('/dashboard/manager') &&
            router.query?.page === 'personal-info' && (
              <Button
                // size="small"
                disabled
                variant="contained"
                startIcon={<Iconify icon={'eva:settings-2-outline'} />}
                color="inherit"
                sx={{
                  boxShadow: 0,
                  width: '100%',
                }}
              >
                <Typography variant="inherit" noWrap>
                  {t('nav.account.manager')}
                </Typography>
              </Button>
            )}
          {!(
            router.pathname.includes('/dashboard/manager') && router.query?.page === 'personal-info'
          ) && (
            <NextLink
              href={{
                pathname: '/dashboard/manager/personal-info',
                query: getFromObject(router)
                  ? {
                      from: getFromObject(router),
                    }
                  : undefined,
              }}
              passHref
            >
              <Button
                // size="small"
                disabled={
                  router.pathname.includes('/dashboard/manager') &&
                  router.query?.page === 'personal-info'
                }
                variant="contained"
                startIcon={<Iconify icon={'eva:settings-2-outline'} />}
                onClick={onClick}
                color="inherit"
                sx={{
                  boxShadow: 0,
                  width: '100%',
                }}
              >
                <Typography variant="inherit" noWrap>
                  {t('nav.account.manager')}
                </Typography>
              </Button>
            </NextLink>
          )}
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<Iconify icon={'majesticons:logout'} />}
            onClick={logout}
          >
            {t('nav.account.logout')}
          </Button>
        </Stack>
      </Card>
    </Box>
  );
}
